/**
 * Which shift roles owe a plan item: ADR-0035 Amendment 8 (OMON-362).
 *
 * ONE shape for developmental goals and monitored events, flowing librarian →
 * planner → carer:
 *   - the catalogue definition carries a default role set
 *     (`_shiftRolesDefault`, default none) and may be locked (`_rolesLocked`);
 *   - a plan item is seeded from that default and ALWAYS written with its
 *     `_shiftRoles`, plus `_mandatory` = "owed by at least one role" as a
 *     derived shadow for readers that predate roles (production's carer app,
 *     app.dsr) until they move to `rolesOf`;
 *   - a locked definition's defaults win over the plan item (see
 *     `effectiveRoles`), so a lock reaches every existing plan without
 *     rewriting published versions.
 *
 * The Shift log is simply a definition defaulted to all four and locked. No
 * code here knows its name (am.7's minimum-set special case is gone).
 *
 * The role CHECKBOXES show off production only (`shiftRolesAvailable`): there,
 * production carers cannot yet choose a role, so the planner keeps one
 * Mandatory switch that sets all four roles or none.
 */
import { type AppEnv } from "../env-gate";
import {
  ALL_SHIFT_ROLE_CODES,
  defaultsOf,
  effectiveRoles,
  rolesLocked,
  type ShiftRoleCode,
} from "../shift-roles";

type Definition = Record<string, unknown> | null | undefined;

/** A builder goal or monitored event, as far as roles are concerned. */
export interface RoleBearing {
  mandatory?: boolean;
  shiftRoles?: ShiftRoleCode[];
}

/** The role checkboxes show everywhere since 2026-10-14 (Ed): planners set
 * who owes each item now, so plans are already right when carers can choose
 * their role. Kept as a function so the one call site stays readable. */
export function shiftRolesAvailable(_env: AppEnv): boolean {
  return true;
}

/** Can carers choose their role on a shift? Everywhere since planner 1.5.17
 * (Ed, 2026-10-16), live together with the carer app's per-person completion
 * (1.5.5) and Chase judging each carer by their role (app.dsr
 * `perPersonJudging`): a ticked role now narrows what each carer owes, and
 * the help text says so. Kept as a function of the build's env so a future
 * environment can differ without touching the call sites. */
export function carersChooseRoles(_env: AppEnv): boolean {
  return true;
}

/** The line under the "Owed by" checkboxes. Must not promise a narrowing
 * the carer app and Chase can't yet honour. */
export function ownedByHelp(carersChoose: boolean): string {
  return carersChoose
    ? "Mandatory for carers in a ticked role (they choose their role in the carer app). Anyone can still record it."
    : "Until carers can choose their role in the app, ticking any role means the shift owes this record. Untick every role to make it optional.";
}

/** Valid, de-duplicated role codes in display order; anything else is dropped. */
export function normaliseRoles(raw: unknown): ShiftRoleCode[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return ALL_SHIFT_ROLE_CODES.filter((c) => raw.includes(c));
}

/** Roles a new plan item starts with: the librarian's default. */
export function seedRoles(definition: Definition): ShiftRoleCode[] {
  return defaultsOf(definition);
}

/** The roles that owe this builder item, lock applied. */
export function itemRoles(item: RoleBearing, definition: Definition): ShiftRoleCode[] {
  return effectiveRoles(
    { _shiftRoles: item.shiftRoles, _mandatory: item.mandatory } as Record<string, unknown>,
    definition,
  );
}

/** Is this item's role set fixed by the librarian? */
export function isLocked(definition: Definition): boolean {
  return rolesLocked(definition);
}

function setRoles(item: RoleBearing, roles: readonly ShiftRoleCode[]): void {
  item.shiftRoles = ALL_SHIFT_ROLE_CODES.filter((c) => roles.includes(c));
  item.mandatory = item.shiftRoles.length > 0;
}

/** Tick or untick one role. A locked item does not change. */
export function toggleRole(item: RoleBearing, role: ShiftRoleCode, definition: Definition): void {
  if (isLocked(definition)) return;
  const current = itemRoles(item, definition);
  setRoles(item, current.includes(role) ? current.filter((r) => r !== role) : [...current, role]);
}

/** Production's single Mandatory switch: every role, or none. Locked: no change. */
export function setMandatoryForAll(item: RoleBearing, on: boolean, definition: Definition): void {
  if (isLocked(definition)) return;
  setRoles(item, on ? ALL_SHIFT_ROLE_CODES : []);
}

/** The fields written onto the plan item: always explicit (am.8). */
export function roleFields(item: RoleBearing, definition: Definition): { _shiftRoles: ShiftRoleCode[]; _mandatory: boolean } {
  const roles = itemRoles(item, definition);
  return { _shiftRoles: roles, _mandatory: roles.length > 0 };
}

/** Does the item's role set differ from the librarian's default? */
export function differsFromDefault(item: RoleBearing, definition: Definition): boolean {
  const a = itemRoles(item, definition);
  const b = defaultsOf(definition);
  return a.length !== b.length || a.some((r) => !b.includes(r));
}

/** What a given role owes on a shift. */
export function owedByRole<T extends RoleBearing>(
  items: readonly T[],
  role: ShiftRoleCode,
  definitionOf: (item: T) => Definition,
): T[] {
  return items.filter((i) => itemRoles(i, definitionOf(i)).includes(role));
}
